// Hook para búsqueda de productos
// Maneja la búsqueda de productos con debounce y cache

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

// Tipos para la búsqueda de productos
export interface ProductSearchResult {
  product: {
    id: string;
    name: string;
    slug: string;
    description: string;
    unitOfMeasurement: string;
    images: Array<{
      url: string;
      alt: string;
      order: number;
    }> | null;
    brand: string;
    outstanding: boolean;
    active: boolean;
    order: number;
    categoryId: string;
    seoTitle: string;
    seoDescription: string;
    seoKeywords: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    createdBy: string | null;
    updatedBy: string | null;
    isDeleted: boolean;
    isActive: boolean;
    imageCount: number;
    hasImages: boolean;
    primaryImage: {
      url: string;
      alt: string;
      order: number;
    } | null;
  };
  relevance: number;
}

export interface ProductSearchResponse {
  success: boolean;
  data: ProductSearchResult[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    query: string;
    searchTime: number;
  };
}

export interface ProductSearchParams {
  query: string;
  page?: number;
  limit?: number;
}

// Hook principal para búsqueda de productos
export function useProductSearch(params: ProductSearchParams) {
  const { query, page = 1, limit = 10 } = params;

  return useQuery({
    queryKey: ['product', 'search', query, page, limit],
    queryFn: async (): Promise<ProductSearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.append('query', query);
      searchParams.append('page', page.toString());
      searchParams.append('limit', limit.toString());

      const response = await apiClient.get<ProductSearchResponse>(
        `/product/search?${searchParams.toString()}`
      );
      return response;
    },
    enabled: query.length >= 2, // Solo buscar si hay al menos 2 caracteres
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos (antes era cacheTime)
  });
}

// Hook para búsqueda con debounce (para la barra de búsqueda)
export function useProductSearchWithDebounce(
  query: string,
  debounceMs: number = 300
) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  return useProductSearch({
    query: debouncedQuery,
    page: 1,
    limit: 10,
  });
}

// Hook para búsqueda rápida (sugerencias)
export function useProductSearchSuggestions(query: string) {
  return useProductSearch({
    query,
    page: 1,
    limit: 5, // Solo 5 sugerencias
  });
}

// Hook para búsqueda completa (página de resultados)
export function useProductSearchResults(
  query: string,
  page: number = 1,
  limit: number = 12
) {
  return useProductSearch({
    query,
    page,
    limit,
  });
}

// Funciones de utilidad
export function getProductSearchUrl(query: string, page: number = 1) {
  const params = new URLSearchParams();
  params.append('q', query);
  if (page > 1) {
    params.append('page', page.toString());
  }
  return `/search?${params.toString()}`;
}

export function formatSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

export function isValidSearchQuery(query: string): boolean {
  return query.trim().length >= 2;
}
